import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FaHandshake, FaLightbulb, FaBolt, FaShieldAlt } from "react-icons/fa";

const About = () => {
  const values = [
    {
      icon: <FaHandshake className="text-4xl text-blue-500" />,
      title: "Partnership First",
      description: "We don't just close a ticket and move on. We build long-term relationships and grow with your business."
    },
    {
      icon: <FaLightbulb className="text-4xl text-blue-500" />,
      title: "Honest Advice",
      description: "We recommend only what your business truly needs. No upselling, no fluff."
    },
    {
      icon: <FaBolt className="text-4xl text-blue-500" />,
      title: "Speed & Reliability",
      description: "We know downtime costs money. Our team responds fast and resolves faster."
    },
    {
      icon: <FaShieldAlt className="text-4xl text-blue-500" />,
      title: "Security Always",
      description: "Every solution we deliver has security built in — not bolted on."
    }
  ];

  return (
    <div className="pt-20">
      {/* Header Section */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">About DevOpsMitra</h1>
            <p className="text-xl sm:text-2xl text-slate-300 italic">
              "Mitra" means friend in Hindi — and that's exactly what we are to your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900">Our Story</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  DevOpsMitra was founded with a simple belief: small and medium businesses deserve the same quality of IT support as large enterprises — without the enterprise price tag.
                </p>
                <p>
                  We saw too many growing businesses in Bangalore struggling with unreliable vendors, overpriced hardware, and IT teams that didn't understand their real needs. So we built DevOpsMitra — a one-stop IT mitra that combines consulting, procurement, cloud, networking, cybersecurity, and DevOps expertise under one roof.
                </p>
                <p>
                  Today, we proudly serve SMBs across Bangalore and pan-India, helping them build secure, efficient, and scalable technology foundations.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&q=80&w=800" 
                alt="Our team"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900">Our Mission</h2>
            <p className="text-lg sm:text-xl text-slate-600">
              To empower small and medium businesses with reliable, affordable, and future-ready IT solutions — delivered with the trust of a long-term mitra.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-slate-900">Our Values</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md border border-slate-100 transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Let's Build Something Great Together
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-slate-300 max-w-2xl mx-auto">
              We're based in Bangalore and work with businesses across India. Reach out today for a free consultation.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="contact-us-btn"
                className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
              >
                Contact Us →
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
